/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/naming-convention */
import {
    Component,
    ViewChild,
    ElementRef,
    AfterViewInit,
    OnDestroy,
    OnChanges,
    SimpleChanges,
    Input,
    Output,
    EventEmitter
} from '@angular/core';
import { Subject, takeUntil, take } from 'rxjs';
import { Chart, ChartConfiguration, registerables, ScatterDataPoint } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-date-fns';
import { MeasurementDataService } from '../../services/measurement-data.service';
import {
    MeasurementData,
    Frame,
    Marker,
    MarkerAnnotation,
    CHART_COLORS,
    CHART_CONFIG,
    ChartDataPoint
} from '../../models/measurement-view.types';
import { ControlSettings } from '../control-panel/control-panel.component';
import { format, addSeconds } from 'date-fns';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MarkerAnnotationModalComponent } from '../marker-annotation-modal/marker-annotation-modal.component';
import { MarkerAnnotationModalData } from '../marker-annotation-modal/marker-annotation-modal.model';

type CustomInfoBox = {
    draw: boolean;
    x: number;
    y: number;
    timestamp?: string;
    tofValue?: string;
    t1TrefValue?: string;
    responseValue?: string;
    noiseIndicator?: string;
    unreliableIndicator?: string;
};

type MarkerBox = {
    topLeft: { x: number; y: number };
    bottomRight: { x: number; y: number };
};

type ChartMarker = {
    num: number;
    time: number;
    annotation: string;
    box?: MarkerBox;
};

type ChartWithCustomInfo = {
    customInfoBox?: CustomInfoBox;
    markers?: ChartMarker[];
} & Chart;

// Register Chart.js components
Chart.register(...registerables, zoomPlugin);

@Component({
    selector: 'app-trend-graph',
    templateUrl: './trend-graph.component.html',
    styleUrls: ['./trend-graph.component.scss'],
    standalone: false
})
export class TrendGraphComponent implements AfterViewInit, OnDestroy, OnChanges {
    @ViewChild('trendCanvas', { static: true })
    public canvasRef!: ElementRef<HTMLCanvasElement>;

    @Input() public settings!: ControlSettings;
    @Input() public staticMode = false; // When true, disables zoom, pan, and tooltips for printing

    @Output() public zoomStateChanged = new EventEmitter<{
        isMaxZoom: boolean;
        isMinZoom: boolean;
        isLeftEdge: boolean;
        isRightEdge: boolean;
    }>();

    private chart!: Chart;
    private destroy$ = new Subject<void>();
    private measurement: MeasurementData | null = null;
    private originalRange = 0;
    private originalMin = 0;
    private originalMax = 0;
    private crosshairX: number | null = null; // Track crosshair position

    // Frame caches — rebuilt once per measurement load, eliminating per-hover .filter() calls
    private cachedFrames: Frame[] = [];
    private cachedFramesByLabel = new Map<string, Frame[]>();

    // rAF throttle for onHover
    private hoverRafId: number | null = null;
    private pendingMouseX: number | null = null;

    // Track selected point in select mode
    private selectedPoint: {
        datasetIndex: number;
        dataIndex: number;
        element: { x: number; y: number };
    } | null = null;

    // Track last hovered point in hover mode for sticky behavior
    private lastHoveredPoint: {
        datasetIndex: number;
        dataIndex: number;
        element: { x: number; y: number };
    } | null = null;

    private maxAmplitude = 10;
    private firstTofcZeroTime: number | null = null;

    // Focus state for wheel zoom — wheel zoom is only active after the user clicks the chart
    public isChartFocused = false;
    private documentClickHandler: ((e: MouseEvent) => void) | null = null;

    // Zoom/Pan state for UI
    public isMaxZoom = false;
    public isMinZoom = true;
    public isLeftEdge = true;
    public isRightEdge = true;

    public constructor(
        private measurementDataService: MeasurementDataService,
        private modalService: NgbModal
    ) {}

    /**
     * Format seconds as HH:mm:ss
     */
    private formatSecondsAsTime(totalSeconds: number): string {
        const seconds = Math.floor(totalSeconds);
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }

    public ngAfterViewInit(): void {
        // Listen for measurement updates from the service
        this.measurementDataService.currentMeasurement$.pipe(takeUntil(this.destroy$)).subscribe((measurement) => {
            if (measurement && this.settings) {
                this.measurement = measurement;
                this.buildFrameCache();
                this.updateMaxAmplitude();
                this.createChart();
            }
        });

        this.setupWheelFocusBehavior();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.hoverRafId !== null) {
            cancelAnimationFrame(this.hoverRafId);
        }
        if (this.chart) {
            this.chart.destroy();
        }
        if (this.documentClickHandler) {
            document.removeEventListener('click', this.documentClickHandler, true);
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        // Handle settings changes
        if (changes['settings']) {
            const prevSettings = changes['settings'].previousValue as ControlSettings;
            const currentSettings = changes['settings'].currentValue as ControlSettings;

            // If chart doesn't exist yet, it will be created in ngAfterViewInit with correct settings
            if (!this.chart) {
                return;
            }

            // If selection mode changed, clear selected point
            if (prevSettings && prevSettings.selectionMode !== currentSettings.selectionMode) {
                this.selectedPoint = null;
                this.lastHoveredPoint = null;
                this.crosshairX = null;
            }

            // If time format or selection mode changed, recreate the entire chart
            // (selection mode affects event handlers, time format affects data structure)
            if (
                prevSettings &&
                (prevSettings.timeFormat !== currentSettings.timeFormat ||
                    prevSettings.selectionMode !== currentSettings.selectionMode)
            ) {
                this.createChart();
                return;
            }

            // Otherwise just update amplitude dataset point sizes
            const amplitudeDataset = this.chart.data
                .datasets[3] as ChartConfiguration<'scatter'>['data']['datasets'][0];
            if (amplitudeDataset) {
                amplitudeDataset.pointRadius = this.getAmplitudePointSize();
                amplitudeDataset.pointHoverRadius = this.getAmplitudePointSize();
                this.chart.update('none'); // Update without animation
            }
        }
    }

    public zoomIn(): void {
        if (this.chart) {
            this.chart.zoom(1.25); // Zoom in by 25%
            setTimeout(() => this.emitZoomState(), 100); // Small delay to let zoom complete
        }
    }

    public zoomOut(): void {
        if (this.chart) {
            this.chart.zoom(0.8); // Zoom out by 20%
            setTimeout(() => this.emitZoomState(), 100); // Small delay to let zoom complete
        }
    }

    public resetZoom(): void {
        if (this.chart) {
            this.chart.resetZoom();
            this.emitZoomState();
        }
    }

    public panLeft(): void {
        if (this.chart) {
            this.chart.pan({ x: 50 }); // Pan chart right to show earlier data
            this.emitZoomState();
        }
    }

    public panRight(): void {
        if (this.chart) {
            this.chart.pan({ x: -50 }); // Pan chart left to show later data
            this.emitZoomState();
        }
    }

    private emitZoomState(): void {
        if (!this.chart) return;

        const scales = this.chart.scales;
        const xScale = scales['x'];

        // Get the current zoom level by comparing current range to original range
        const currentRange = xScale.max - xScale.min;

        // For debugging, let's use a simple approach first
        // We'll consider it "zoomed" if the current range is significantly smaller than when first created
        const isZoomed = currentRange < this.originalRange * 0.95; // 5% smaller means zoomed

        const isMaxZoom = currentRange < this.originalRange * 0.1; // Very zoomed in
        const isMinZoom = !isZoomed; // Not zoomed = at minimum

        // For pan detection, check if we're at the edges
        // Use a small threshold relative to current range for better accuracy when zoomed
        const edgeThreshold = Math.min(currentRange * 0.02, this.originalRange * 0.01);
        const isLeftEdge = xScale.min <= this.originalMin + edgeThreshold;
        const isRightEdge = xScale.max >= this.originalMax - edgeThreshold;

        // Update local state for UI
        this.isMaxZoom = isMaxZoom;
        this.isMinZoom = isMinZoom;
        this.isLeftEdge = isLeftEdge;
        this.isRightEdge = isRightEdge;

        this.zoomStateChanged.emit({
            isMaxZoom,
            isMinZoom,
            isLeftEdge,
            isRightEdge
        });
    }

    private updateMaxAmplitude(): void {
        if (!this.measurement) return;

        const maxAmpl = this.measurementDataService.getMaxAmplitude(this.cachedFrames);
        this.maxAmplitude = Math.ceil(maxAmpl) + 1;
    }

    private shouldShowT1Tref(frame: Frame): boolean {
        if (!frame.t1Tref || frame.t1Tref <= 0) return false;
        if (frame.time >= 600) return false;
        if (this.firstTofcZeroTime === null) return true;
        return frame.time - this.firstTofcZeroTime < 60;
    }

    private buildFrameCache(): void {
        if (!this.measurement) return;
        this.cachedFrames = this.measurement.data.filter(
            (entry): entry is Frame =>
                entry.type === 'frame' &&
                entry.frame_type !== 'PTC-ST' &&
                entry.frame_type !== 'CAL' &&
                entry.frame_type !== 'CAL_UNPROCESSED'
        );
        // Cache the time of the first TOFC=0 result for T1/Tc display logic
        const firstTofcZero = this.cachedFrames.find(
            (f) => (f.frame_type === 'TOFC' || f.frame_type === 'PTC-TOFC') && f.value === 0
        );
        this.firstTofcZeroTime = firstTofcZero?.time ?? null;
        const compound = this.cachedFrames.filter(
            (f) =>
                f.frame_type === 'PTC' ||
                f.frame_type === 'TOFR' ||
                f.frame_type === 'TOFC' ||
                f.frame_type === 'PTC-TOFR' ||
                f.frame_type === 'PTC-TOFC'
        );
        this.cachedFramesByLabel.set(
            'TOFR',
            compound.filter((f) => f.frame_type === 'TOFR' || f.frame_type === 'PTC-TOFR')
        );
        this.cachedFramesByLabel.set(
            'TOFC',
            compound.filter((f) => f.frame_type === 'TOFC' || f.frame_type === 'PTC-TOFC')
        );
        this.cachedFramesByLabel.set(
            'PTC',
            compound.filter((f) => f.frame_type === 'PTC')
        );
        this.cachedFramesByLabel.set('Amplitude', this.cachedFrames);
        this.cachedFramesByLabel.set(
            'ST',
            this.cachedFrames.filter((f) => f.frame_type === 'ST' && f.t1Tref != null /*&& f.t1Tref > 0*/)
        );
    }

    /** Binary search: returns the index in a sorted ScatterDataPoint[] closest to targetX */
    private findClosestIndex(data: ScatterDataPoint[], targetX: number): number {
        if (data.length === 0) return -1;
        let lo = 0;
        let hi = data.length - 1;
        while (lo < hi) {
            const mid = (lo + hi) >> 1;
            if (data[mid].x < targetX) lo = mid + 1;
            else hi = mid;
        }
        if (lo > 0 && Math.abs(data[lo - 1].x - targetX) <= Math.abs(data[lo].x - targetX)) {
            return lo - 1;
        }
        return lo;
    }

    /**
     * Update tooltip display for a specific data point
     */
    private updateTooltipForPoint(
        chart: Chart,
        datasetIndex: number,
        dataIndex: number,
        element: { x: number; y: number }
    ): void {
        const dataset = chart.data.datasets[datasetIndex];

        // Amplitude points don't show tooltips — ST has its own dedicated dataset
        if (dataset.label === 'Amplitude') {
            const infoBox = (chart as ChartWithCustomInfo).customInfoBox;
            if (infoBox) {
                infoBox.draw = false;
                chart.update('none');
            }
            return;
        }

        // Update custom info box for TOF datasets
        const infoBox = (chart as ChartWithCustomInfo).customInfoBox;
        infoBox.draw = true;

        // Store position for tooltip positioning at the data point
        infoBox.x = element.x;
        infoBox.y = element.y;

        // Get timestamp
        const dataPoint = chart.data.datasets[datasetIndex].data[dataIndex] as ScatterDataPoint;

        if (this.settings.timeFormat === 'absolute') {
            infoBox.timestamp = format(new Date(dataPoint.x), 'HH:mm:ss');
        } else {
            infoBox.timestamp = this.formatSecondsAsTime(Number(dataPoint.x));
        }

        // Clear previous values
        infoBox.tofValue = '';
        infoBox.t1TrefValue = '';
        infoBox.responseValue = '';
        infoBox.noiseIndicator = '';
        infoBox.unreliableIndicator = '';

        // Get the scaled value from the point
        const scaledYValue = dataPoint.y;

        // Use getOriginalValue to convert scaled chart value back to original value
        const originalValue = this.getOriginalValue(scaledYValue, dataset.label || '');

        // Get the target frame to check for noise
        const frameForNoise = this.findFrameByDataPoint(dataset.label || '', dataIndex);
        const isNoise = frameForNoise?.wave_data?.is_noise || false;

        // For noise points, show simplified tooltip with just NOISE and "-"
        if (isNoise) {
            infoBox.tofValue = 'NOISE';
            infoBox.responseValue = '-';
            infoBox.noiseIndicator = '';
            infoBox.t1TrefValue = '';
        } else {
            // Set TOF value based on the specific dataset
            switch (dataset.label) {
                case 'TOFR':
                    infoBox.tofValue = `TOF Ratio: ${Math.round(frameForNoise?.value || 0)} %`;
                    break;
                case 'TOFC':
                    infoBox.tofValue = `TOF Count: ${Math.round(originalValue)}`;
                    break;
                case 'PTC':
                    infoBox.tofValue = `PTC Count: ${Math.round(originalValue)}`;
                    break;
                case 'PTC-TOFR':
                    infoBox.tofValue = `PTC-TOF Ratio: ${Math.round(frameForNoise?.value || 0)} %`;
                    break;
                case 'PTC-TOFC':
                    infoBox.tofValue = `PTC-TOF Count: ${Math.round(originalValue)}`;
                    break;
                case 'ST':
                    infoBox.tofValue = `ST T1/Tc: ${Math.round(frameForNoise?.t1Tref ?? 0)} %`;
                    break;
            }

            // Show T1/Tc for TOFR/PTC-TOFR when display conditions are met
            if (
                (dataset.label === 'TOFR' || dataset.label === 'PTC-TOFR') &&
                frameForNoise &&
                this.shouldShowT1Tref(frameForNoise)
            ) {
                infoBox.t1TrefValue = `T1/Tc: ${Math.round(frameForNoise.t1Tref ?? 0)} %`;
            } else {
                infoBox.t1TrefValue = '';
            }

            // Always show response/amplitude data from frame if available
            const targetFrame = frameForNoise;

            if (targetFrame?.wave_data?.amplitude !== undefined) {
                const amplitude = targetFrame.wave_data.amplitude;
                infoBox.responseValue = `Response: ${this.measurementDataService.roundValue(amplitude)} mV`;
            }

            infoBox.noiseIndicator = '';

            // Show unreliable indicator when warning_flags is set (TOFR, TOFC, ST datasets only)
            if (
                (dataset.label === 'TOFR' || dataset.label === 'TOFC' || dataset.label === 'ST') &&
                frameForNoise?.warning_flags
            ) {
                infoBox.unreliableIndicator = 'Unreliable';
            }
        }

        chart.update('none');
    }

    /**
     * Process a data point selection and return the frame to select
     * Handles both amplitude and TOF dataset points
     */
    private processDataPointSelection(datasetIndex: number, dataIndex: number, chart: Chart): Frame | null {
        const dataset = chart.data.datasets[datasetIndex];

        // Amplitude points are not selectable — ST has its own dedicated dataset
        if (dataset.label === 'Amplitude') {
            return null;
        }

        // For TOF datasets, find the corresponding frame
        return this.findFrameByDataPoint(dataset.label || '', dataIndex);
    }

    private createChart(): void {
        if (!this.measurement) return;

        // Clear selected point when creating new chart
        this.selectedPoint = null;
        this.lastHoveredPoint = null;
        this.crosshairX = null;

        // Get data from service with time format preference
        const useRelativeTime = this.settings.timeFormat === 'relative';
        const trendData = this.measurementDataService.getTrendGraphData(this.measurement, useRelativeTime);

        // Parse start time from measurement meta data
        const startDate = new Date(`${this.measurement.meta.date}T${this.measurement.meta.time}`);
        const endDate = addSeconds(startDate, this.measurement.meta.duration);
        const g_x_axis_padding_s = 10; // Extra seconds added to the right of the last data point
        const g_x_axis_padding_ms = g_x_axis_padding_s * 1000;

        // Constants matching original implementation (adjusted for better proportions)
        const g_trend_ptc_area_height_pct = CHART_CONFIG.TREND_PTC_AREA_HEIGHT_PCT;
        const g_trend_tofc_area_height_pct = CHART_CONFIG.TREND_TOFC_AREA_HEIGHT_PCT;
        const g_trend_tofr_area_start_pct = g_trend_ptc_area_height_pct + g_trend_tofc_area_height_pct;
        const g_trend_tofr_area_height_pct = 100 - g_trend_tofr_area_start_pct;
        const g_trend_noise_area_height_pct = 7.5; // Noise section below PTC 0
        const g_noise_y_value = -(g_trend_noise_area_height_pct / 2); // Fixed Y position for all noise points

        // Custom plugin for drawing multiple axes (like original implementation)
        const customAxesPlugin = {
            id: 'customAxes',
            beforeInit: (chart: Chart) => {
                // Initialize markers from measurement data
                const chartWithMarkers = chart as ChartWithCustomInfo;
                if (!chartWithMarkers.markers) {
                    chartWithMarkers.markers = [];

                    // Extract markers from measurement data
                    const markers =
                        this.measurement?.data.filter((entry): entry is Marker => entry.type === 'marker') || [];
                    const markerAnnotations = this.measurement?.dynamicData?.marker_annotations || [];

                    if (markers.length > 0) {
                        for (const marker of markers) {
                            const annotation =
                                markerAnnotations.find((ann: MarkerAnnotation) => ann.marker_num === marker.number)
                                    ?.annotation || '';

                            chartWithMarkers.markers.push({
                                num: marker.number,
                                time: marker.time,
                                annotation: annotation
                            });
                        }
                    }
                }
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            beforeEvent: (chart: Chart, args: any) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                const { event } = args;
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                if (event.type === 'click') {
                    const chartWithMarkers = chart as ChartWithCustomInfo;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    const x = event.x as number;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    const y = event.y as number;

                    // Check if click is on a marker
                    const clickedMarker = chartWithMarkers.markers?.find((marker) => {
                        if (marker.box) {
                            const { topLeft, bottomRight } = marker.box;
                            return x >= topLeft.x && x <= bottomRight.x && y >= topLeft.y && y <= bottomRight.y;
                        }
                        return false;
                    });

                    if (clickedMarker) {
                        this.openMarkerAnnotationModal([
                            {
                                number: clickedMarker.num,
                                time: clickedMarker.time,
                                annotation: clickedMarker.annotation
                            }
                        ]);
                    }
                }
            },
            afterDatasetsDraw: (chart: Chart) => {
                const ctx = chart.ctx;
                const xScale = chart.scales['x'];
                const yScale = chart.scales['y'];

                const xpos_lines = 52 + 8;
                const xpos_text_left = 34;
                const xpos_text_right = xScale.getPixelForDecimal(1) + 8;

                ctx.font = '9px Arial';

                // Draw TOFR y-scale line and range labels (top section: 40-100%)
                ctx.lineWidth = 3;
                ctx.beginPath();
                ctx.strokeStyle = CHART_COLORS.TOFR;
                ctx.moveTo(xpos_lines, yScale.getPixelForValue(100));
                ctx.lineTo(xpos_lines, yScale.getPixelForValue(g_trend_tofr_area_start_pct));
                ctx.stroke();
                ctx.fillStyle = CHART_COLORS.TOFR;
                ctx.fillText('110', xpos_text_left, yScale.getPixelForValue(100));
                ctx.fillText('  0', xpos_text_left, yScale.getPixelForValue(g_trend_tofr_area_start_pct + 3));

                // Draw TOFC y-scale line and range labels (middle section: 20-40%)
                ctx.beginPath();
                ctx.strokeStyle = CHART_COLORS.TOFC;
                ctx.moveTo(xpos_lines, yScale.getPixelForValue(g_trend_tofr_area_start_pct));
                ctx.lineTo(xpos_lines, yScale.getPixelForValue(g_trend_ptc_area_height_pct));
                ctx.stroke();
                ctx.fillStyle = CHART_COLORS.TOFC;
                ctx.fillText('  3', xpos_text_left, yScale.getPixelForValue(g_trend_tofr_area_start_pct - 3));
                ctx.fillText('  0', xpos_text_left, yScale.getPixelForValue(g_trend_ptc_area_height_pct + 3));

                // Draw PTC y-scale line and range labels (bottom section: 0-20%)
                ctx.beginPath();
                ctx.strokeStyle = CHART_COLORS.PTC;
                ctx.moveTo(xpos_lines, yScale.getPixelForValue(g_trend_ptc_area_height_pct));
                ctx.lineTo(xpos_lines, yScale.getPixelForValue(0));
                ctx.stroke();
                ctx.fillStyle = CHART_COLORS.PTC;
                ctx.fillText(' 20', xpos_text_left, yScale.getPixelForValue(g_trend_ptc_area_height_pct - 3));
                ctx.fillText('  0', xpos_text_left, yScale.getPixelForValue(0));

                // Draw NOISE y-scale line (below PTC 0)
                ctx.beginPath();
                ctx.strokeStyle = CHART_COLORS.NOISE;
                ctx.moveTo(xpos_lines, yScale.getPixelForValue(0));
                ctx.lineTo(xpos_lines, yScale.getPixelForValue(-g_trend_noise_area_height_pct));
                ctx.stroke();

                // Draw amplitude scale values on right (matching original positioning)
                const mid1 = (this.maxAmplitude * 2) / 3;
                const mid2 = this.maxAmplitude / 3;
                const roundedMid1 = this.measurementDataService.roundValue(mid1);
                const roundedMid2 = this.measurementDataService.roundValue(mid2);
                const roundedMax = this.measurementDataService.roundValue(this.maxAmplitude);

                ctx.fillStyle = CHART_COLORS.AMPLITUDE;
                ctx.fillText(roundedMid1.toString(), xpos_text_right, yScale.getPixelForValue((100 * 2) / 3));
                ctx.fillText(roundedMid2.toString(), xpos_text_right, yScale.getPixelForValue(100 / 3));
                ctx.fillText('0', xpos_text_right, yScale.getPixelForValue(0));
                ctx.fillText(`${roundedMax} (mV)`, xpos_text_right, yScale.getPixelForValue(100) + 7);

                // Draw horizontal grid lines (matching original)
                ctx.strokeStyle = CHART_COLORS.TIMELINE;
                ctx.lineWidth = 1;

                // Main section dividers
                [
                    100,
                    g_trend_tofr_area_start_pct,
                    g_trend_ptc_area_height_pct,
                    0,
                    -g_trend_noise_area_height_pct
                ].forEach((value) => {
                    ctx.beginPath();
                    ctx.moveTo(chart.chartArea.left, yScale.getPixelForValue(value));
                    ctx.lineTo(chart.chartArea.right, yScale.getPixelForValue(value));
                    ctx.stroke();
                });

                // Draw right-side y-axis border line (amplitude color)
                ctx.strokeStyle = CHART_COLORS.AMPLITUDE;
                ctx.beginPath();
                ctx.moveTo(chart.chartArea.right, yScale.getPixelForValue(100));
                ctx.lineTo(chart.chartArea.right, yScale.getPixelForValue(-g_trend_noise_area_height_pct));
                ctx.stroke();

                // Draw marker symbols and text
                const chartWithMarkers = chart as ChartWithCustomInfo;
                const markers = chartWithMarkers.markers || [];
                if (markers.length > 0) {
                    const markerBaseYPosValue = 103;
                    let previousRightEdge: number | undefined;

                    ctx.beginPath();
                    ctx.lineWidth = 1;
                    ctx.strokeStyle = '#808080';
                    ctx.fillStyle = 'rgba(120, 120, 120, 1.0)';
                    ctx.font = '12px Arial';

                    for (const marker of markers) {
                        // Calculate marker position based on time format
                        let m_xpos: number;
                        if (useRelativeTime) {
                            // For relative time, marker.time is already in seconds
                            m_xpos = xScale.getPixelForValue(marker.time);
                        } else {
                            // For absolute time, convert marker time to milliseconds from start
                            m_xpos = xScale.getPixelForValue(startDate.getTime() + marker.time * 1000);
                        }

                        // Skip if marker is outside visible area
                        if (m_xpos < chart.chartArea.left || m_xpos > chart.chartArea.right) {
                            continue;
                        }

                        const text = 'Marker ' + marker.num;
                        const textWidth = ctx.measureText(text).width;
                        const rightEdge = m_xpos + 7;
                        const leftEdgeWithText = m_xpos - textWidth - 9;
                        const willOverlap = previousRightEdge && previousRightEdge >= leftEdgeWithText;
                        const leftEdge = willOverlap ? m_xpos - 7 : leftEdgeWithText;

                        // Store marker box for click detection
                        marker.box = {
                            topLeft: {
                                x: leftEdge,
                                y: yScale.getPixelForValue(markerBaseYPosValue + 6)
                            },
                            bottomRight: {
                                x: rightEdge,
                                y: yScale.getPixelForValue(markerBaseYPosValue - 2)
                            }
                        };

                        // Draw marker arrow symbol
                        ctx.moveTo(m_xpos - 3, yScale.getPixelForValue(markerBaseYPosValue + 3));
                        ctx.lineTo(m_xpos, yScale.getPixelForValue(markerBaseYPosValue + 1));
                        ctx.lineTo(m_xpos + 3, yScale.getPixelForValue(markerBaseYPosValue + 3));
                        ctx.stroke();

                        // Draw marker text if it won't overlap
                        if (!willOverlap) {
                            ctx.fillText(text, m_xpos - textWidth - 6, yScale.getPixelForValue(markerBaseYPosValue));
                        }

                        previousRightEdge = rightEdge;
                    }
                }

                // Draw custom info box if hover is active
                const infoBox = (chart as ChartWithCustomInfo).customInfoBox;
                if (infoBox?.draw && infoBox.x !== undefined && infoBox.y !== undefined) {
                    // Calculate tooltip content and size
                    const lines: string[] = [];
                    if (infoBox.timestamp) lines.push(infoBox.timestamp);
                    if (infoBox.tofValue) lines.push(infoBox.tofValue);
                    if (infoBox.t1TrefValue) lines.push(infoBox.t1TrefValue);
                    if (infoBox.responseValue) lines.push(infoBox.responseValue);
                    if (infoBox.noiseIndicator) lines.push(infoBox.noiseIndicator);
                    if (infoBox.unreliableIndicator) lines.push(infoBox.unreliableIndicator);

                    if (lines.length > 0) {
                        ctx.font = '11px Arial';
                        const lineHeight = 14;
                        const padding = 12; // Increased padding for longer text

                        // Measure each line with proper font context
                        const textWidths = lines.map((line, index) => {
                            // Ensure font is set before measuring
                            ctx.font = '11px Arial';
                            const textWidth = ctx.measureText(line).width;

                            // Add space for colored square indicator (except for timestamp)
                            if (index > 0) {
                                return textWidth + 8 + 6; // Square size (8px) + gap (6px)
                            }
                            return textWidth;
                        });

                        const maxWidth = Math.max(...textWidths);

                        // Add extra padding for longer response values
                        const boxWidth = maxWidth + padding * 2 + 4; // Extra 4px buffer
                        const boxHeight = lines.length * lineHeight + padding * 1.5;

                        // Position tooltip near the data point with offset to avoid overlapping
                        let boxX = infoBox.x + 15; // Offset to the right of point
                        let boxY = infoBox.y - boxHeight - 10; // Above the point

                        // Ensure tooltip stays within chart bounds
                        if (boxX + boxWidth > chart.chartArea.right) {
                            boxX = infoBox.x - boxWidth - 15; // Position to the left
                        }
                        if (boxY < chart.chartArea.top) {
                            boxY = infoBox.y + 15; // Position below the point
                        }

                        // Ensure we don't go outside chart area
                        boxX = Math.max(chart.chartArea.left, Math.min(boxX, chart.chartArea.right - boxWidth));
                        boxY = Math.max(chart.chartArea.top, Math.min(boxY, chart.chartArea.bottom - boxHeight));

                        // Draw tooltip background with rounded corners
                        ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
                        ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
                        ctx.strokeStyle = '#666';
                        ctx.lineWidth = 1;
                        ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

                        // Draw text lines with colored indicators
                        let lineY = boxY + padding + 11; // 11px from top for baseline

                        lines.forEach((line, index) => {
                            // Ensure consistent font for drawing
                            ctx.font = '11px Arial';
                            ctx.fillStyle = 'white'; // All text is white

                            let textX = boxX + padding;

                            // Draw colored square indicator for data lines (not timestamp)
                            if (index > 0) {
                                const squareSize = 8;
                                const squareY = lineY - squareSize + 1; // Align with text baseline

                                if (line === 'NOISE') {
                                    // White square for NOISE line
                                    ctx.fillStyle = 'white';
                                } else if (line === '-') {
                                    // Orange square for "-" line (response value in noise tooltips)
                                    ctx.fillStyle = CHART_COLORS.AMPLITUDE;
                                } else if (line.includes('ST T1/Tc')) {
                                    // Green square for ST T1/Tc data
                                    ctx.fillStyle = CHART_COLORS.ST;
                                } else if (line.includes('T1/Tc')) {
                                    ctx.fillStyle = CHART_COLORS.TOFR;
                                } else if (line.includes('TOF Ratio') || line.includes('PTC-TOF Ratio')) {
                                    // Blue square for TOFR data (matches TOFR point color)
                                    ctx.fillStyle = CHART_COLORS.TOFR;
                                } else if (line.includes('TOF Count') || line.includes('PTC-TOF Count')) {
                                    // Orange square for TOFC data (matches TOFC point color)
                                    ctx.fillStyle = CHART_COLORS.TOFC;
                                } else if (line.includes('PTC Count')) {
                                    // Red square for PTC data (matches PTC point color)
                                    ctx.fillStyle = CHART_COLORS.PTC;
                                } else if (line.includes('Response')) {
                                    // Orange square for Response data (always amplitude color)
                                    ctx.fillStyle = CHART_COLORS.AMPLITUDE;
                                }

                                // Draw the colored square (not for Unreliable — text only)
                                if (line !== 'Unreliable') {
                                    ctx.fillRect(textX, squareY, squareSize, squareSize);

                                    // Add black border for white squares only
                                    if (line === 'NOISE') {
                                        ctx.strokeStyle = 'black';
                                        ctx.lineWidth = 1;
                                        ctx.strokeRect(textX, squareY, squareSize, squareSize);
                                        ctx.lineWidth = 1; // Reset line width
                                    }

                                    // Adjust text position to after the square
                                    textX += squareSize + 6; // Square width + 6px gap
                                }

                                // Reset to white for text
                                ctx.fillStyle = 'white';
                            }

                            ctx.fillText(line, textX, lineY);
                            lineY += lineHeight;
                        });
                    }
                }

                // Draw vertical text labels (like original)
                ctx.font = '11px Arial';
                ctx.fillStyle = 'rgba(80, 80, 80, 0.5)';
                ctx.fillText('Trend', 0, 10 + ctx.lineWidth);

                ctx.font = 'bold 9px Arial';
                ctx.fillStyle = CHART_COLORS.TOFR;
                const containsST = (this.cachedFramesByLabel.get('ST')?.length ?? 0) > 0;
                const tofrLabelPos =
                    g_trend_tofr_area_start_pct + g_trend_tofr_area_height_pct * (containsST ? 0.6 : 0.5);
                ctx.fillText('TOFR', 0, yScale.getPixelForValue(tofrLabelPos) + 9 + ctx.lineWidth);
                ctx.fillText('(%)', 0, yScale.getPixelForValue(tofrLabelPos) + 9 + ctx.lineWidth + 15);
                if (containsST) {
                    ctx.fillStyle = CHART_COLORS.ST;
                    ctx.fillText('ST', 0, yScale.getPixelForValue(tofrLabelPos) + 9 + ctx.lineWidth + 30);
                    ctx.fillText('T1/Tc', 0, yScale.getPixelForValue(tofrLabelPos) + 9 + ctx.lineWidth + 45);
                    ctx.fillText('(%)', 0, yScale.getPixelForValue(tofrLabelPos) + 9 + ctx.lineWidth + 60);
                }

                ctx.fillStyle = CHART_COLORS.TOFC;
                const tofcLabelPos = g_trend_ptc_area_height_pct + g_trend_tofc_area_height_pct * 0.5;
                ctx.fillText('TOFC', 0, yScale.getPixelForValue(tofcLabelPos) + 9 + ctx.lineWidth);

                ctx.fillStyle = CHART_COLORS.PTC;
                const ptcLabelPos = g_trend_ptc_area_height_pct * 0.5;
                ctx.fillText('PTC', 0, yScale.getPixelForValue(ptcLabelPos) + 9 + ctx.lineWidth);

                ctx.fillStyle = CHART_COLORS.NOISE;
                const noiseLabelPos = -(g_trend_noise_area_height_pct / 2);
                ctx.fillText('NOISE', 0, yScale.getPixelForValue(noiseLabelPos) + 9 + ctx.lineWidth);

                // Draw vertical crosshair line if hovering over a data point
                if (this.crosshairX !== null) {
                    ctx.save();
                    ctx.strokeStyle = 'rgba(100, 100, 100, 0.8)'; // Gray dotted line
                    ctx.lineWidth = 1;
                    ctx.setLineDash([4, 4]); // Dotted line pattern

                    ctx.beginPath();
                    ctx.moveTo(this.crosshairX, chart.chartArea.top);
                    ctx.lineTo(this.crosshairX, chart.chartArea.bottom);
                    ctx.stroke();

                    ctx.restore(); // Restore canvas state
                }
            }
        };

        // Pre-compute static color arrays once — avoids per-render callback overhead for large datasets
        const tofPointSize = this.getTofPointSize();
        const amplitudePointSize = this.getAmplitudePointSize();

        const buildColors = (label: string, color: string, defaultBorderWidth: number, checkUnreliable = false) => {
            const frames = this.cachedFramesByLabel.get(label) ?? this.cachedFrames;
            return {
                backgroundColor: frames.map((f) => {
                    if (f.wave_data?.is_noise) return CHART_COLORS.NOISE;
                    if (checkUnreliable && f.warning_flags) return 'transparent';
                    return color;
                }),
                borderColor: frames.map((f) => (f.wave_data?.is_noise ? CHART_COLORS.NOISE : color)),
                borderWidth: frames.map((f) => {
                    if (f.wave_data?.is_noise) return 1;
                    if (checkUnreliable && f.warning_flags) return 1;
                    return defaultBorderWidth;
                })
            };
        };

        const tofrColors = buildColors('TOFR', CHART_COLORS.TOFR, 1, true);
        const tofcColors = buildColors('TOFC', CHART_COLORS.TOFC, 1, true);
        const ptcColors = buildColors('PTC', CHART_COLORS.PTC, 1);
        const amplitudeColors = buildColors('Amplitude', CHART_COLORS.AMPLITUDE, 1);
        const amplitudeFrames = this.cachedFramesByLabel.get('Amplitude') ?? this.cachedFrames;
        const amplitudePointRadii = amplitudeFrames.map((f) => (f.wave_data?.is_noise ? 0 : amplitudePointSize));
        const stColors = buildColors('ST', CHART_COLORS.ST, 1, true);

        const tofrFrames = this.cachedFramesByLabel.get('TOFR') ?? [];
        const tofcFrames = this.cachedFramesByLabel.get('TOFC') ?? [];
        const ptcFrames = this.cachedFramesByLabel.get('PTC') ?? [];
        const stFrames = this.cachedFramesByLabel.get('ST') ?? [];

        const config: ChartConfiguration<'scatter'> = {
            type: 'scatter',
            data: {
                datasets: [
                    {
                        label: 'TOFR',
                        data: trendData.tofr.map(
                            (point: ChartDataPoint, index: number): ScatterDataPoint => ({
                                x: point.x as number,
                                y: tofrFrames[index]?.wave_data?.is_noise
                                    ? g_noise_y_value
                                    : this.measurementDataService.getTofrDataSeriesValue(point.y)
                            })
                        ),
                        backgroundColor: tofrColors.backgroundColor,
                        borderColor: tofrColors.borderColor,
                        borderWidth: tofrColors.borderWidth,
                        pointRadius: tofPointSize,
                        pointHoverRadius: tofPointSize
                    },
                    {
                        label: 'TOFC',
                        data: trendData.tofc.map(
                            (point: ChartDataPoint, index: number): ScatterDataPoint => ({
                                x: point.x as number,
                                y: tofcFrames[index]?.wave_data?.is_noise
                                    ? g_noise_y_value
                                    : this.measurementDataService.getTofcDataSeriesValue(point.y)
                            })
                        ),
                        backgroundColor: tofcColors.backgroundColor,
                        borderColor: tofcColors.borderColor,
                        borderWidth: tofcColors.borderWidth,
                        pointRadius: tofPointSize,
                        pointHoverRadius: tofPointSize
                    },
                    {
                        label: 'PTC',
                        data: trendData.ptc.map(
                            (point: ChartDataPoint, index: number): ScatterDataPoint => ({
                                x: point.x as number,
                                y: ptcFrames[index]?.wave_data?.is_noise
                                    ? g_noise_y_value
                                    : this.measurementDataService.getPtcDataSeriesValue(point.y)
                            })
                        ),
                        backgroundColor: ptcColors.backgroundColor,
                        borderColor: ptcColors.borderColor,
                        borderWidth: ptcColors.borderWidth,
                        pointRadius: tofPointSize,
                        pointHoverRadius: tofPointSize
                    },
                    {
                        label: 'Amplitude',
                        data: trendData.amplitude.map(
                            (point: ChartDataPoint): ScatterDataPoint => ({
                                x: point.x as number,
                                y: Math.round((100.0 * point.y) / this.maxAmplitude)
                            })
                        ),
                        backgroundColor: amplitudeColors.backgroundColor,
                        borderColor: amplitudeColors.borderColor,
                        borderWidth: amplitudeColors.borderWidth,
                        pointRadius: amplitudePointRadii,
                        pointHoverRadius: amplitudePointRadii,
                        pointStyle: 'crossRot'
                    },
                    {
                        label: 'ST',
                        data: trendData.st.map(
                            (point: ChartDataPoint, index: number): ScatterDataPoint => ({
                                x: point.x as number,
                                y: stFrames[index]?.wave_data?.is_noise
                                    ? g_noise_y_value
                                    : this.measurementDataService.getTofrDataSeriesValue(point.y)
                            })
                        ),
                        backgroundColor: stColors.backgroundColor,
                        borderColor: stColors.borderColor,
                        borderWidth: stColors.borderWidth,
                        pointRadius: tofPointSize,
                        pointHoverRadius: tofPointSize
                    }
                ]
            },
            plugins: [customAxesPlugin],
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: { duration: 250 },
                layout: {
                    padding: { left: 60, right: 60, top: 36, bottom: 0 } // Match original layout
                },
                events: ['mousemove', 'mouseout', 'click', 'touchstart'],
                scales: {
                    x:
                        this.settings.timeFormat === 'absolute'
                            ? {
                                  type: 'time',
                                  time: {
                                      unit: 'second',
                                      displayFormats: {
                                          second: 'HH:mm:ss'
                                      }
                                  },
                                  min: startDate.getTime(),
                                  max: endDate.getTime() + g_x_axis_padding_ms,
                                  position: 'bottom',
                                  display: true,
                                  ticks: {
                                      maxRotation: 0,
                                      maxTicksLimit: 10,
                                      font: { size: 10 },
                                      color: CHART_COLORS.TIMELINE
                                  },
                                  grid: {
                                      display: true,
                                      color: 'rgba(0, 0, 0, 0.1)',
                                      drawOnChartArea: true
                                  }
                              }
                            : {
                                  type: 'linear',
                                  min: 0,
                                  max: this.measurement.meta.duration + g_x_axis_padding_s,
                                  position: 'bottom',
                                  display: true,
                                  ticks: {
                                      maxRotation: 0,
                                      maxTicksLimit: 10,
                                      font: { size: 10 },
                                      color: CHART_COLORS.TIMELINE,
                                      callback: (value: string | number) => {
                                          return this.formatSecondsAsTime(Number(value));
                                      }
                                  },
                                  grid: {
                                      display: true,
                                      color: 'rgba(0, 0, 0, 0.1)',
                                      drawOnChartArea: true
                                  }
                              },
                    y: {
                        type: 'linear',
                        min: -g_trend_noise_area_height_pct,
                        max: 100,
                        display: false, // Hide default Y-axis since we draw custom ones
                        grid: {
                            display: true,
                            color: 'rgba(90, 90, 90, 1.0)'
                        }
                    }
                },
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        enabled: false // Disable default tooltip to prevent duplicates
                    },
                    zoom: {
                        pan: {
                            enabled: !this.staticMode,
                            mode: 'x',
                            threshold: 10,
                            onPanComplete: () => {
                                // Update zoom state after panning with mouse
                                setTimeout(() => this.emitZoomState(), 0);
                            }
                        },
                        zoom: {
                            wheel: { enabled: !this.staticMode && this.isChartFocused, speed: 0.1 },
                            drag: { enabled: !this.staticMode, modifierKey: 'shift' },
                            pinch: { enabled: !this.staticMode },
                            mode: 'x',
                            onZoomComplete: () => {
                                // Update zoom state after zooming with mouse
                                setTimeout(() => this.emitZoomState(), 0);
                            }
                        },
                        limits: {
                            x: {
                                min: this.settings.timeFormat === 'absolute' ? startDate.valueOf() : 0,
                                max:
                                    this.settings.timeFormat === 'absolute'
                                        ? endDate.valueOf() + g_x_axis_padding_ms
                                        : this.measurement.meta.duration + g_x_axis_padding_s,
                                minRange:
                                    this.settings.timeFormat === 'absolute'
                                        ? (endDate.valueOf() - startDate.valueOf()) / CHART_CONFIG.MAX_ZOOM
                                        : this.measurement.meta.duration / CHART_CONFIG.MAX_ZOOM
                            }
                        }
                    }
                },
                onHover: (event, elements, chart) => {
                    // Skip hover interactions in static mode
                    if (this.staticMode) {
                        return;
                    }

                    // SELECT MODE: Only show tooltip/crosshair for selected point (ignore hover)
                    if (this.settings.selectionMode === 'select') {
                        if (this.selectedPoint) {
                            this.updateTooltipForPoint(
                                chart,
                                this.selectedPoint.datasetIndex,
                                this.selectedPoint.dataIndex,
                                this.selectedPoint.element
                            );
                        }
                        return;
                    }

                    // HOVER MODE — capture mouseX immediately, throttle processing to one rAF per frame
                    const mouseX =
                        event && typeof event === 'object' && 'x' in event ? (event as { x: number }).x : undefined;

                    if (mouseX === undefined || mouseX === null) {
                        return;
                    }

                    this.pendingMouseX = mouseX;

                    if (this.hoverRafId !== null) {
                        return; // already queued for this frame
                    }

                    this.hoverRafId = requestAnimationFrame(() => {
                        this.hoverRafId = null;
                        const mx = this.pendingMouseX;
                        if (mx === null) return;

                        // Convert pixel X to data X for binary search
                        const dataX = chart.scales['x'].getValueForPixel(mx) ?? mx;

                        // Binary search each dataset for the closest point — O(log n) per dataset
                        let closestDistance = Infinity;
                        let closestPointInfo: {
                            datasetIndex: number;
                            dataIndex: number;
                            element: { x: number; y: number };
                        } | null = null;

                        chart.data.datasets.forEach((dataset, datasetIndex) => {
                            if (dataset.label === 'Amplitude') return; // Amplitude is visual only; ST/TOF datasets handle tooltips
                            const data = dataset.data as ScatterDataPoint[];
                            if (!data?.length) return;
                            const idx = this.findClosestIndex(data, dataX);
                            if (idx < 0) return;
                            const element = chart.getDatasetMeta(datasetIndex).data[idx];
                            if (element?.x !== undefined) {
                                const distance = Math.abs(mx - element.x);
                                if (distance < closestDistance) {
                                    closestDistance = distance;
                                    closestPointInfo = {
                                        datasetIndex,
                                        dataIndex: idx,
                                        element: { x: element.x, y: element.y }
                                    };
                                }
                            }
                        });

                        if (closestPointInfo) {
                            let shouldSwitch = false;

                            if (!this.lastHoveredPoint) {
                                shouldSwitch = true;
                            } else {
                                const isDifferentPoint =
                                    this.lastHoveredPoint.datasetIndex !== closestPointInfo.datasetIndex ||
                                    this.lastHoveredPoint.dataIndex !== closestPointInfo.dataIndex;

                                if (isDifferentPoint) {
                                    const currentPointDistance = Math.abs(mx - this.lastHoveredPoint.element.x);
                                    if (closestDistance < currentPointDistance) {
                                        shouldSwitch = true;
                                    }
                                }
                            }

                            if (shouldSwitch) {
                                this.lastHoveredPoint = closestPointInfo;
                                this.crosshairX = closestPointInfo.element.x;

                                this.updateTooltipForPoint(
                                    chart,
                                    closestPointInfo.datasetIndex,
                                    closestPointInfo.dataIndex,
                                    closestPointInfo.element
                                );

                                const targetFrame = this.processDataPointSelection(
                                    closestPointInfo.datasetIndex,
                                    closestPointInfo.dataIndex,
                                    chart
                                );
                                if (targetFrame) {
                                    this.measurementDataService.selectFrame(targetFrame);
                                }
                            }
                        } else if (this.lastHoveredPoint) {
                            this.updateTooltipForPoint(
                                chart,
                                this.lastHoveredPoint.datasetIndex,
                                this.lastHoveredPoint.dataIndex,
                                this.lastHoveredPoint.element
                            );
                        }
                    });
                },
                onClick: (event, elements, chart) => {
                    // Only select frame on click if in select mode
                    if (this.settings.selectionMode === 'select') {
                        const mouseX =
                            event && typeof event === 'object' && 'x' in event ? (event as { x: number }).x : undefined;

                        if (mouseX === undefined || mouseX === null) {
                            return;
                        }

                        // Binary search each dataset for the closest point — O(log n) per dataset
                        const dataX = chart.scales['x'].getValueForPixel(mouseX) ?? mouseX;
                        let closestDistance = Infinity;
                        let closestPointInfo: {
                            datasetIndex: number;
                            dataIndex: number;
                            element: { x: number; y: number };
                        } | null = null;

                        chart.data.datasets.forEach((dataset, datasetIndex) => {
                            if (dataset.label === 'Amplitude') return; // Amplitude is visual only; ST/TOF datasets handle selection
                            const data = dataset.data as ScatterDataPoint[];
                            if (!data?.length) return;
                            const idx = this.findClosestIndex(data, dataX);
                            if (idx < 0) return;
                            const element = chart.getDatasetMeta(datasetIndex).data[idx];
                            if (element?.x !== undefined) {
                                const distance = Math.abs(mouseX - element.x);
                                if (distance < closestDistance) {
                                    closestDistance = distance;
                                    closestPointInfo = {
                                        datasetIndex,
                                        dataIndex: idx,
                                        element: { x: element.x, y: element.y }
                                    };
                                }
                            }
                        });

                        if (closestPointInfo) {
                            this.selectedPoint = closestPointInfo;
                            this.crosshairX = closestPointInfo.element.x;

                            this.updateTooltipForPoint(
                                chart,
                                closestPointInfo.datasetIndex,
                                closestPointInfo.dataIndex,
                                closestPointInfo.element
                            );

                            const targetFrame = this.processDataPointSelection(
                                closestPointInfo.datasetIndex,
                                closestPointInfo.dataIndex,
                                chart
                            );
                            if (targetFrame) {
                                this.measurementDataService.selectFrame(targetFrame);
                            }
                        }
                    }
                }
            }
        };

        if (this.chart) {
            this.chart.destroy();
        }

        // Ensure canvas is clean
        const ctx = this.canvasRef.nativeElement.getContext('2d');
        if (ctx) {
            ctx.clearRect(0, 0, this.canvasRef.nativeElement.width, this.canvasRef.nativeElement.height);
        }

        this.chart = new Chart(this.canvasRef.nativeElement, config);

        // Store original values for zoom state detection
        if (this.settings.timeFormat === 'absolute') {
            this.originalMin = startDate.valueOf();
            this.originalMax = endDate.valueOf() + g_x_axis_padding_ms;
        } else {
            this.originalMin = 0;
            this.originalMax = this.measurement.meta.duration + g_x_axis_padding_s;
        }
        this.originalRange = this.originalMax - this.originalMin;

        // Emit initial zoom state
        setTimeout(() => this.emitZoomState(), 0);

        // Initialize custom info box
        (this.chart as ChartWithCustomInfo).customInfoBox = {
            draw: false,
            x: 0,
            y: 0,
            tofValue: '',
            t1TrefValue: '',
            responseValue: '',
            timestamp: '',
            noiseIndicator: ''
        };
    }

    private setupWheelFocusBehavior(): void {
        const canvas = this.canvasRef.nativeElement;
        const container = canvas.closest('.trend-graph-container');

        canvas.addEventListener('click', () => {
            this.setChartFocused(true);
        });

        this.documentClickHandler = (e: MouseEvent) => {
            if (container && !container.contains(e.target as Node)) {
                this.setChartFocused(false);
            }
        };
        document.addEventListener('click', this.documentClickHandler, true);
    }

    private setChartFocused(focused: boolean): void {
        this.isChartFocused = focused;
        if (this.chart && !this.staticMode) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
            (this.chart.options.plugins as any).zoom.zoom.wheel.enabled = focused;
            this.chart.update('none');
        }
    }

    private getAmplitudePointSize(): number {
        switch (this.settings.amplitudeSize) {
            case 'disabled':
                return 0;
            case 'small':
                return 1;
            case 'medium':
                return 2;
            case 'large':
                return 4;
            default:
                return 2;
        }
    }

    /**
     * Get TOF point size using automatic sizing logic from original implementation
     * TOF points (TOFR, TOFC, PTC) use this sizing, not user preference
     */
    private getTofPointSize(): number {
        // Original logic: g_plot_size = dataSeriesData.perc_ampl.length > 200 ? 1 : 3
        return this.cachedFrames.length > 200 ? 2 : 2;
    }

    private getOriginalValue(scaledValue: number, datasetLabel: string): number {
        const g_trend_ptc_area_height_pct = CHART_CONFIG.TREND_PTC_AREA_HEIGHT_PCT;
        const g_trend_tofc_area_height_pct = CHART_CONFIG.TREND_TOFC_AREA_HEIGHT_PCT;
        const g_trend_tofr_area_start_pct = g_trend_ptc_area_height_pct + g_trend_tofc_area_height_pct;
        const g_trend_tofr_area_height_pct = 100 - g_trend_tofr_area_start_pct;
        const g_max_tofr_percentage = CHART_CONFIG.MAX_TOFR_PERCENTAGE;

        switch (datasetLabel) {
            case 'TOFR':
                // Reverse the TOFR calculation: scaledValue = trendTofrAreaStartPct + (trendTofrAreaHeightPct * value / maxTofrPercentage)
                return Math.round(
                    ((scaledValue - g_trend_tofr_area_start_pct) * g_max_tofr_percentage) / g_trend_tofr_area_height_pct
                );
            case 'ST':
                // Same range and scaling as TOFR (0-110%)
                return Math.round(
                    ((scaledValue - g_trend_tofr_area_start_pct) * g_max_tofr_percentage) / g_trend_tofr_area_height_pct
                );
            case 'TOFC':
                // Reverse the TOFC calculation: scaledValue = ptcAreaHeight + (tofcAreaHeight * value / 4)
                return Math.round(((scaledValue - g_trend_ptc_area_height_pct) * 4) / g_trend_tofc_area_height_pct);
            case 'PTC':
                // Reverse the PTC calculation: scaledValue = ptcAreaHeight * (value / 20)
                return Math.round((scaledValue * 20) / g_trend_ptc_area_height_pct);
            case 'Amplitude':
                return (scaledValue * this.maxAmplitude) / 100;
            default:
                return scaledValue;
        }
    }

    /**
     * Find the correct frame based on dataset label and index (uses pre-built cache — O(1))
     */
    private findFrameByDataPoint(datasetLabel: string, dataIndex: number): Frame | null {
        const frames = this.cachedFramesByLabel.get(datasetLabel) ?? this.cachedFrames;
        return frames[dataIndex] ?? null;
    }

    /**
     * Get frame for a specific dataset point index (used for noise detection styling)
     */
    private getFrameForDatasetPoint(datasetLabel: string, dataIndex: number): Frame | null {
        return this.findFrameByDataPoint(datasetLabel, dataIndex);
    }

    /**
     * Open marker annotation modal to display one or more markers
     */
    private openMarkerAnnotationModal(markers: { number: number; time: number; annotation: string }[]): void {
        const modalRef = this.modalService.open(MarkerAnnotationModalComponent, {
            size: 'lg'
        });
        const modalData: MarkerAnnotationModalData = {
            measurementId: this.measurement.dynamicData.measurementId,
            markers: markers
        };
        (modalRef.componentInstance as MarkerAnnotationModalComponent).data = modalData;

        // Handle modal close and update service with changes
        modalRef.closed.pipe(take(1)).subscribe((hasChanges: boolean) => {
            if (hasChanges) {
                const modalMarkers = (modalRef.componentInstance as MarkerAnnotationModalComponent).data.markers;
                const markerAnnotations = modalMarkers.map((m) => ({
                    marker_num: m.number,
                    time: m.time,
                    annotation: m.annotation
                }));
                this.measurementDataService.updateMarkerAnnotations(markerAnnotations);

                // Update markers in chart
                const chartWithMarkers = this.chart as ChartWithCustomInfo;
                if (chartWithMarkers.markers) {
                    chartWithMarkers.markers = markerAnnotations.map((ann) => ({
                        num: ann.marker_num,
                        time: ann.time,
                        annotation: ann.annotation
                    }));
                    this.chart.draw();
                }
            }
        });
    }
}
